import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { fetchMyProfile, submitAssessment } from '../../lib/apiClient';
import {
  clearPendingAssessment,
  readPendingAssessment,
} from '../../lib/assessment/submission';
import { routes } from '../../lib/assessment/routes';
import { usePageMeta } from '../../lib/usePageMeta';
import { Button } from '../ui/button';
import { ProfileNameForm } from './ProfileNameForm';

type ClaimState = 'idle' | 'claiming' | 'error' | 'limit-reached' | 'naming';

/**
 * Claim handler. When a just-signed-in user has a pending anonymous assessment, submit
 * it with the SAME submissionId so the claim is idempotent (a retry never creates a
 * second attempt/result). pendingAssessment is cleared ONLY after a 200/201 success,
 * so a transient failure can be retried safely — and the retry reuses the same stored
 * submissionId. The claim is a stable callback invoked both on mount and by Retry, so a
 * retry genuinely re-runs the submission (a plain re-navigation would not).
 */
export function Welcome() {
  const { status, session } = useAuth();
  const [, navigate] = useLocation();
  const token = session?.access_token ?? null;
  usePageMeta({ title: 'Welcome | High Performer OS', noindex: true });

  const [state, setState] = useState<ClaimState>('idle');
  /** Where to go once the customer has named themselves. */
  const [nextPath, setNextPath] = useState<string | null>(null);
  const claimingRef = useRef(false);

  const claim = useCallback(async () => {
    if (status !== 'signed-in' || !token || claimingRef.current) return;
    const pending = readPendingAssessment(window.sessionStorage);
    if (!pending) {
      // Nothing to claim — a direct visit, or a second arrival after the claim
      // already succeeded and cleared the marker. A signed-in visitor belongs
      // at their dashboard, not at the marketing home.
      navigate(routes.dashboard);
      return;
    }
    claimingRef.current = true;
    setState('claiming');
    try {
      const result = await submitAssessment(token, pending.type, pending.submissionId, pending.answers);
      if (result.state === 'ok') {
        clearPendingAssessment(window.sessionStorage);
        /*
          Production asks for a name here, once, before letting the customer
          through. A profile that already carries one goes straight on, so a
          returning customer never sees the form twice. A failed profile read
          also goes straight on: a name is worth asking for, not worth blocking
          somebody's result over.
        */
        const profile = await fetchMyProfile(token).catch(() => null);
        const named =
          profile?.state === 'ok' &&
          Boolean(profile.profile.first_name) &&
          Boolean(profile.profile.last_name);

        if (!named && profile?.state === 'ok') {
          setNextPath(routes.result(result.resultId));
          setState('naming');
          return;
        }
        navigate(routes.result(result.resultId));
        return;
      }
      if (result.state === 'limit-reached') {
        clearPendingAssessment(window.sessionStorage);
        setState('limit-reached');
        return;
      }
      // Returned error state: keep the pending payload (with its submissionId) for Retry.
      setState('error');
    } catch {
      // Rejected promise (network/transport failure): keep pending for Retry; never
      // leave an unhandled rejection or a stuck "claiming" state.
      setState('error');
    } finally {
      // Runs on every outcome (success return, limit-reached, returned error, or a
      // rejected promise), so Retry can always invoke another claim.
      claimingRef.current = false;
    }
  }, [status, token, navigate]);

  useEffect(() => {
    void claim();
  }, [claim]);

  if (status !== 'signed-in') {
    return <p className="text-muted-foreground">Sign in to continue.</p>;
  }
  if (state === 'error') {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-destructive">
          We could not save your assessment results. Please try again.
        </p>
        <Button
          type="button"
          className="self-start"
          onClick={() => {
            void claim();
          }}
        >
          Retry
        </Button>
      </section>
    );
  }
  if (state === 'limit-reached') {
    return (
      <p className="text-foreground">You have used all of your free attempts for that assessment.</p>
    );
  }

  /*
    Production asks for a name here, after the result is claimed and before the
    customer continues. V2 never collected one, so every surface that prints a
    name — the dashboard greeting, the report's "Prepared for", the workbook
    cover, the admin Users table — was permanently blank.
  */
  if (state === 'naming' && token) {
    return (
      <ProfileNameForm
        token={token}
        onSaved={() => navigate(nextPath ?? routes.dashboard)}
      />
    );
  }

  return <p className="text-muted-foreground">Saving your result…</p>;
}
